import { useState, useEffect } from 'react';
import { useTransition, animated } from 'react-spring';
import { formatNumber } from '@belong/common';
import { Text, BulletList } from '@belong/ui';
import clsx from 'clsx';
import { UnitListingDetailsModel } from 'src/api/models';
import { FavoriteButton } from 'src/features/listing-page/components/favorite-button/favorite-button';
import type { NormalizedFavorites } from 'src/features/listing-page/types';
import {
  getBathRoomString,
  getFormattedBeds,
} from 'src/features/listing-page/utils';

type StickyBannerProps = {
  home: UnitListingDetailsModel;
  normalizedFavoritesByHomeId: NormalizedFavorites;
};

function MobileStickyBannerComponent({
  home,
  style,
  normalizedFavoritesByHomeId,
}: StickyBannerProps & { style: React.CSSProperties }) {
  const { basicInfo } = home;

  return (
    <div
      style={{ ...style, height: 60 }}
      className={clsx('z-fullscreen fixed top-0 block w-full lg:hidden')}
    >
      <div className="flex h-full justify-end">
        <div className="gap-sm pr-sm flex h-full items-center bg-white">
          <Text
            as="div"
            variant="p1"
            fontWeight="semibold"
            className="leading-p2"
          >
            {/* TODO: Looks like this is used in a lot of places (global search getFormattedBeds and you will find them), maybe make a component? */}
            <BulletList
              wrap
              items={[
                getFormattedBeds(basicInfo),
                // `!` throughout: this banner only renders inside the house
                // page, which already dereferences `basicInfo` unconditionally.
                getBathRoomString(basicInfo!),
                <span key="sqft">
                  {formatNumber(basicInfo!.areaInSquareFeet!)} ft<sup>2</sup>
                </span>,
              ]}
            />
          </Text>
          <FavoriteButton
            homeId={basicInfo!.unitId!}
            isFavorite={!!normalizedFavoritesByHomeId[basicInfo!.unitId!]}
          />
        </div>
      </div>
    </div>
  );
}

export function MobileStickyBanner({
  home,
  normalizedFavoritesByHomeId,
}: StickyBannerProps) {
  const AnimatedMobileStickyBanner = animated(MobileStickyBannerComponent);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window?.scrollY > 0) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const transitions = useTransition(scrolled, {
    from: { y: -100 },
    enter: { y: 0 },
    leave: { y: -100 },
  });

  return transitions(
    (styles, isVisible) =>
      isVisible && (
        <AnimatedMobileStickyBanner
          home={home}
          style={styles}
          normalizedFavoritesByHomeId={normalizedFavoritesByHomeId}
        />
      ),
  );
}
